import axios from 'axios';

import createUrlWithParams from 'helpers/createUrlWithParams';

import {
  parseActionLibraryInstanceListForFE,
  parseAllActionLibraryInstancesForFE,
  parseBulkCreateActionLibraryInstanceForBE,
  parseDelegateActionLibraryInstanceForBE,
  parseInstanceActivityLogListForFE,
  parseInstanceCommentCreateForBE,
  parseInstanceCommentForFE,
  parseInstanceCommentListForFE,
  parseInstanceDashboardMetricsForFE,
  parseUpdateActionLibraryInstanceForBE,
} from './actionLibraryInstance.parsers';
import type {
  ActionLibraryInstanceAPI,
  ActionLibraryInstanceDTO,
  InstanceActivityLogEntryDTO,
  InstanceCommentDTO,
} from './actionLibraryInstance.types';

const endpoint = 'api/v1/asset-actions/';
const personalActionsEndpoint = 'api/v1/asset-actions/personal-actions/';

type PaginatedListResponse = {
  count?: number;
  next?: string | null;
  previous?: string | null;
  results?: ActionLibraryInstanceDTO[];
};

const parseListResponse = (data: unknown) => {
  if (Array.isArray(data)) {
    return {
      count: data.length,
      next: null,
      previous: null,
      results: parseAllActionLibraryInstancesForFE(data as ActionLibraryInstanceDTO[]),
    };
  }
  const paginated = data as PaginatedListResponse;
  return {
    count: paginated.count ?? 0,
    next: paginated.next ?? null,
    previous: paginated.previous ?? null,
    results: parseAllActionLibraryInstancesForFE(paginated.results ?? []),
  };
};

export const actionLibraryInstancesApi: ActionLibraryInstanceAPI = {
  getAllActionLibraryInstances: async params => {
    const { data } = await axios.get(createUrlWithParams(endpoint, params || {}));
    return { data: parseListResponse(data) };
  },

  getPersonalActionLibraryInstances: async params => {
    const { data } = await axios.get(createUrlWithParams(personalActionsEndpoint, params || {}));
    return { data: parseListResponse(data) };
  },

  getSingleActionLibraryInstance: async id => {
    const { data } = await axios.get(`${endpoint}${id}/`);
    return { data: parseActionLibraryInstanceListForFE(data) };
  },

  deleteActionLibraryInstance: async id => {
    await axios.delete(`${endpoint}${id}/`);
  },

  createBulkActionLibraryInstances: async data => {
    const { data: responseData } = await axios.post(`${endpoint}create_bulk/`, parseBulkCreateActionLibraryInstanceForBE(data));
    return { data: responseData };
  },
  updateActionLibraryInstance: async (id, data) => {
    const { data: responseData } = await axios.patch(`${endpoint}${id}/`, parseUpdateActionLibraryInstanceForBE(data));
    return { data: parseActionLibraryInstanceListForFE(responseData) };
  },

  delegateActionLibraryInstance: async (id, data) => {
    const { data: responseData } = await axios.patch(`${endpoint}${id}/`, parseDelegateActionLibraryInstanceForBE(data));
    return { data: parseActionLibraryInstanceListForFE(responseData) };
  },

  getActionLibraryInstanceMetrics: async params => {
    const { data } = await axios.get(createUrlWithParams(`${endpoint}metrics/`, params || {}));
    return { data: parseInstanceDashboardMetricsForFE(data) };
  },

  getInstanceStatusLogs: async (id, params) => {
    const { data } = await axios.get(createUrlWithParams(`${endpoint}${id}/status-logs/`, params || {}));
    const rawResults = Array.isArray(data) ? data : data.results || [];
    const results = rawResults as InstanceActivityLogEntryDTO[];
    return {
      data: {
        count: Array.isArray(data) ? data.length : data.count ?? results.length,
        next: Array.isArray(data) ? null : data.next ?? null,
        previous: Array.isArray(data) ? null : data.previous ?? null,
        results: parseInstanceActivityLogListForFE(results),
      },
    };
  },

  getInstanceComments: async (id, params) => {
    const { data } = await axios.get(createUrlWithParams(`${endpoint}${id}/comments/`, params || {}));
    const rawResults = Array.isArray(data) ? data : data.results || [];
    const results = rawResults as InstanceCommentDTO[];
    return {
      data: {
        count: Array.isArray(data) ? data.length : data.count ?? results.length,
        next: Array.isArray(data) ? null : data.next ?? null,
        previous: Array.isArray(data) ? null : data.previous ?? null,
        results: parseInstanceCommentListForFE(results),
      },
    };
  },

  createInstanceComment: async (id, payload) => {
    const { data } = await axios.post(`${endpoint}${id}/comments/`, parseInstanceCommentCreateForBE(payload));
    return { data: parseInstanceCommentForFE(data as InstanceCommentDTO) };
  },
};

export default actionLibraryInstancesApi;
